import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import _set from 'lodash/set';
import { Status, States } from '@sift/resift/models/Status';
import {
  SEND_INVITE,
  SEND_INVITE_SUCCESS,
  SEND_INVITE_ERROR,
  SEND_INVITE_ALL,
  SEND_INVITE_ALL_SUCCESS,
  SEND_INVITE_ALL_ERROR,
} from 'store/people';
import { LOCATION_CHANGE } from 'store/location';

export const INVITES_TABLE_USER_SELECT = 'INVITES_TABLE_USER_SELECT';
export const INVITES_TABLE_BULK_SELECT = 'INVITES_TABLE_BULK_SELECT';

export const INVITES_BULK_DIALOG_OPEN = 'INVITES_BULK_DIALOG_OPEN';
export const INVITES_BULK_DIALOG_CLOSE = 'INVITES_BULK_DIALOG_CLOSE';
export const INVITES_BULK_DIALOG_COMPLETE = 'INVITES_BULK_DIALOG_COMPLETE';
export const INVITES_COMPLETE_DIALOG_CLOSE = 'INVITES_COMPLETE_DIALOG_CLOSE';

export const INVITES_FORM_UPDATE = 'INVITES_FORM_UPDATE';
export const INVITES_FORM_RESET = 'INVITES_FORM_RESET';

export const INVITES_SETTINGS_UPDATE = 'INVITES_SETTINGS_UPDATE';

export const INVITES_TEMPLATE_FETCH = 'INVITES_TEMPLATE_FETCH';
export const INVITES_TEMPLATE_FETCH_SUCCESS = 'INVITES_TEMPLATE_FETCH_SUCCESS';
export const INVITES_TEMPLATE_FETCH_ERROR = 'INVITES_TEMPLATE_FETCH_ERROR';

export const INVITES_TEMPLATE_RESET = 'INVITES_TEMPLATE_RESET';

export const INVITES_TEMPLATE_UPDATE = 'INVITES_TEMPLATE_UPDATE';
export const INVITES_TEMPLATE_UPDATE_SUCCESS = 'INVITES_TEMPLATE_UPDATE_SUCCESS';
export const INVITES_TEMPLATE_UPDATE_ERROR = 'INVITES_TEMPLATE_UPDATE_ERROR';

export const INVITES_TEMPLATE_EMAIL_TEST = 'INVITES_TEMPLATE_EMAIL_TEST';
export const INVITES_TEMPLATE_EMAIL_TEST_SUCCESS = 'INVITES_TEMPLATE_EMAIL_TEST_SUCCESS';
export const INVITES_TEMPLATE_EMAIL_TEST_ERROR = 'INVITES_TEMPLATE_EMAIL_TEST_ERROR';

function toggleStatus(key, statusState) {
  return (state, action) => {
    return {
      ...state,
      [key]: new Status(statusState),
    };
  };
}

function createSelectedById(selectedUsers) {
  return selectedUsers.reduce(
    (selectedById, user) => ({
      ...selectedById,
      [user.id]: user,
    }),
    {},
  );
}

export function updateForm(field, value) {
  return {
    type: INVITES_FORM_UPDATE,
    payload: { field, value },
  };
}

export function resetForm() {
  return {
    type: INVITES_FORM_RESET,
  };
}

export function resetTemplate() {
  return {
    type: INVITES_TEMPLATE_RESET,
  };
}

export function selectUser(user) {
  return {
    type: INVITES_TABLE_USER_SELECT,
    payload: user,
  };
}

export function selectAllUsers(users) {
  return {
    type: INVITES_TABLE_BULK_SELECT,
    payload: users,
  };
}

export function openBulkDialog(users) {
  return {
    type: INVITES_BULK_DIALOG_OPEN,
    payload: {
      users,
    },
  };
}

export function closeBulkDialog() {
  return {
    type: INVITES_BULK_DIALOG_CLOSE,
  };
}

export function completeBulkDialog(count) {
  return {
    type: INVITES_BULK_DIALOG_COMPLETE,
    payload: count,
  };
}

export function closeCompleteDialog() {
  return {
    type: INVITES_COMPLETE_DIALOG_CLOSE,
  };
}

export function fetchInviteTemplate() {
  return {
    type: INVITES_TEMPLATE_FETCH,
    payload: {},
  };
}

export function updateEmailTemplate(form) {
  return {
    type: INVITES_TEMPLATE_UPDATE,
    payload: {
      data: {
        model: form,
      },
    },
  };
}

export function testEmailTemplate(template) {
  return {
    type: INVITES_TEMPLATE_EMAIL_TEST,
    payload: {
      data: {
        model: template,
      },
    },
  };
}

export function updateInviteSettings(inviteSettings) {
  return {
    type: INVITES_SETTINGS_UPDATE,
    payload: inviteSettings,
  };
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [INVITES_TABLE_USER_SELECT]: (state, action) => {
    const selectedUserReference = state.selectedUsers[action.payload.id];

    return {
      ...state,
      selectedUsers: {
        ...state.selectedUsers,
        [action.payload.id]: !selectedUserReference ? action.payload : undefined,
      },
      allSelected: false,
    };
  },
  [INVITES_TABLE_BULK_SELECT]: (state, action) => {
    return {
      ...state,
      selectedUsers: state.allSelected ? {} : createSelectedById(action.payload),
      allSelected: !state.allSelected,
    };
  },
  [LOCATION_CHANGE]: state => {
    return {
      ...state,
      selectedUsers: {},
      allSelected: false,
    };
  },
  [INVITES_BULK_DIALOG_OPEN]: (state, action) => {
    return {
      ...state,
      users: action.payload.users || state.selectedUsers,
      sendDialogOpen: true,
    };
  },
  [INVITES_BULK_DIALOG_CLOSE]: (state, action) => {
    return {
      ...state,
      sendDialogOpen: false,
    };
  },
  [INVITES_BULK_DIALOG_COMPLETE]: (state, action) => {
    return {
      ...state,
      sendDialogOpen: false,
      successDialogOpen: true,
      successCount: action.payload,
    };
  },
  [INVITES_COMPLETE_DIALOG_CLOSE]: (state, action) => {
    return {
      ...state,
      successDialogOpen: false,
    };
  },
  [INVITES_FORM_UPDATE]: (state, action) => {
    let newForm = _cloneDeep(state.form);

    newForm = _set(newForm, action.payload.field, action.payload.value);

    return {
      ...state,
      form: newForm,
      isDirty: !_isEqual(newForm, state.initialFormState),
    };
  },
  [INVITES_FORM_RESET]: (state, action) => {
    return {
      ...state,
      form: _cloneDeep(state.initialFormState),
      isDirty: false,
    };
  },
  [INVITES_TEMPLATE_RESET]: (state, action) => {
    const newForm = {
      ...state.form,
      template: state.initialFormState.template,
    };

    return {
      ...state,
      form: {
        ...state.form,
        template: state.initialFormState.template,
      },
      isDirty: !_isEqual(newForm, state.initialFormState),
    };
  },
  [INVITES_TEMPLATE_FETCH]: (state, action) => {
    return {
      ...state,
      templateStatus: new Status(States.LOADING),
    };
  },
  [INVITES_TEMPLATE_FETCH_SUCCESS]: (state, action) => {
    const newInitialFormState = {
      ..._cloneDeep(state.initialFormState),
      template: action.payload.data.model,
    };

    return {
      ...state,
      defaultTemplate: action.payload.data._defaultModel,
      initialFormState: newInitialFormState,
      form: {
        ...state.form,
        template: action.payload.data.model,
      },
      templateStatus: new Status(States.NORMAL),
    };
  },
  [INVITES_TEMPLATE_FETCH_ERROR]: (state, action) => {
    return {
      ...state,
    };
  },
  [INVITES_TEMPLATE_UPDATE]: (state, action) => {
    return {
      ...state,
      templateStatus: new Status(States.LOADING),
    };
  },
  [INVITES_TEMPLATE_UPDATE_SUCCESS]: (state, action) => {
    const newForm = {
      ...state.form,
      template: action.payload.data.model,
    };

    return {
      ...state,
      form: newForm,
      initialFormState: { ...newForm },
      isDirty: false,
      templateStatus: new Status(States.NORMAL),
    };
  },
  [INVITES_SETTINGS_UPDATE]: (state, action) => {
    return {
      ...state,
      form: {
        ...state.form,
        settings: {
          ...action.payload,
        },
      },
      initialFormState: {
        ...state.initialFormState,
        settings: {
          ...action.payload,
        },
      },
      isDirty: false,
    };
  },
  [SEND_INVITE]: toggleStatus('inviteStatus', States.LOADING),
  [SEND_INVITE_SUCCESS]: toggleStatus('inviteStatus', States.NORMAL),
  [SEND_INVITE_ERROR]: toggleStatus('inviteStatus', States.ERROR),
  [SEND_INVITE_ALL]: toggleStatus('inviteStatus', States.LOADING),
  [SEND_INVITE_ALL_SUCCESS]: toggleStatus('inviteStatus', States.NORMAL),
  [SEND_INVITE_ALL_ERROR]: toggleStatus('inviteStatus', States.ERROR),
};

// ------------------------------------
// Reducer
// ------------------------------------
export const initialState = {
  selectedUsers: {},
  allSelected: false,
  sendDialogOpen: false,
  successDialogOpen: false,
  users: 0,
  successCount: 0,
  initialFormState: {
    template: undefined,
  },
  form: {
    template: undefined,
  },
  defaultTemplate: null,
  isDirty: false,
  templateStatus: new Status(States.UNKNOWN),
  inviteStatus: new Status(States.UNKNOWN),
};

export default function InvitesReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}
